import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { HistoryChart } from '../../components/charts';
import { adminApiMessage, evalsApi } from '../adminApi';
import { agentLabel } from '../evals/agents';
import { EvalBadge, EvalError, GateBadge, RunLink } from '../evals/EvalBits';
import { EvalSchedulePanel } from '../evals/EvalSchedulePanel';
import {
  coverageGapsText,
  formatAge,
  formatFigure,
  formatInterval,
  formatUsd,
  gradingLabel,
  metricLabel,
  shortSha,
} from '../evals/format';
import {
  notEvaluatedHeadline,
  partialPointCount,
  promptChangeMarkers,
  slotCoverageText,
  sortPairings,
  sortSlots,
  trendAriaLabel,
  trendChartSeries,
  trendMetrics,
} from '../evals/overviewView';
import type { EvalExpertStatus, EvalMetric, EvalOverview, EvalPairingStatus } from '../evals/types';
import { GuideLink } from '../evals/GuideLink';
import { formatDate, formatInt } from '../format';

// The Evals overview (task eval_admin_runs): is what is deployed evaluated, how
// fresh its evals are, what evaluating has cost, and whether quality drifts.
// A deployed pairing with no covering run leads the page in a "not evaluated"
// state, with the newest run of that model and why it does not count.

const SPEND_WINDOWS = [7, 30, 90];

function PairingRow({ p }: { p: EvalPairingStatus }) {
  const covering = p.covering;
  const latest = p.latest;
  return (
    <tr data-testid={`eval-pairing-${p.role}-${p.suite}-${p.model}`}>
      <td>
        <code className="eval-nowrap">{p.model}</code>
        <div className="admin-muted eval-sub">
          {p.settings.join(', ')}
          {p.aliases.length > 0 ? ` · /model ${p.aliases.join(', ')}` : ''}
        </div>
      </td>
      <td className="eval-nowrap">{agentLabel(p.role)}</td>
      <td className="eval-nowrap">
        {p.suite}
        {p.suite === 'long' ? <span className="admin-muted"> · {p.split}</span> : null}
      </td>
      <td>
        <code>{p.promptVersion}</code>
      </td>
      <td>
        {p.state === 'evaluated' ? (
          <EvalBadge tone="succeeded">Evaluated</EvalBadge>
        ) : (
          <EvalBadge tone="bad" testId="eval-not-evaluated">
            Not evaluated
          </EvalBadge>
        )}
      </td>
      <td className="eval-covering">
        {covering !== null ? (
          <span className="eval-stack">
            <span>
              <RunLink id={covering.runId} /> · {formatAge(covering.ageDays)}
              {covering.gradedFromRunId !== null ? (
                <span className="admin-muted">
                  {' '}
                  · re-grade of <RunLink id={covering.gradedFromRunId} />
                </span>
              ) : null}
            </span>
            <span>
              <GateBadge gate={covering.gate} reason={covering.gateReason} />
            </span>
          </span>
        ) : latest !== null ? (
          <span className="eval-stack">
            <span>
              Newest run <RunLink id={latest.runId} /> · {formatAge(latest.ageDays)}
            </span>
            {latest.gaps.length > 0 ? (
              <span className="admin-muted eval-sub">
                Does not count: {coverageGapsText(latest.gaps, latest.promptVersion)}.
              </span>
            ) : null}
          </span>
        ) : (
          <span className="admin-muted">No stored run of this model</span>
        )}
      </td>
      <td>
        {p.testScore !== null ? (
          <span className="eval-stack">
            <span>
              {metricLabel(p.testScore.metric)}: {formatFigure(p.testScore.figure)}
            </span>
            {p.testScore.figure.ci95 !== null ? (
              <span className="admin-muted eval-sub">
                95% interval {formatInterval(p.testScore.figure.ci95)}
              </span>
            ) : null}
          </span>
        ) : (
          <span className="admin-muted">—</span>
        )}
      </td>
    </tr>
  );
}

function Trends({ overview }: { overview: EvalOverview }) {
  const metrics = trendMetrics(overview.trends);
  const [picked, setPicked] = useState<EvalMetric | null>(null);
  const metric = picked !== null && metrics.includes(picked) ? picked : (metrics[0] ?? null);
  if (metric === null) {
    return (
      <p className="admin-empty">
        No succeeded collect run in the last {overview.trendDays} days, so there is no trend yet.
      </p>
    );
  }
  const series = trendChartSeries(overview.trends, metric);
  const markers = promptChangeMarkers(overview.trends, metric);
  const partial = partialPointCount(overview.trends, metric);
  return (
    <>
      <div className="eval-segments admin-seg" role="tablist" aria-label="Trend metric">
        {metrics.map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={m === metric}
            className="admin-link-button"
            disabled={m === metric}
            onClick={() => setPicked(m)}
          >
            {metricLabel(m)}
          </button>
        ))}
      </div>
      <div className="eval-chart" data-testid="eval-trend-chart">
        <HistoryChart
          series={series}
          markers={markers}
          unit="%"
          domain={[0, 100]}
          valueFormat={(v) => `${Math.round(v)}`}
          markExtremes={false}
          dayBands={false}
          legend
          height={220}
          emptyText="A trend needs runs at two different times."
          ariaLabel={trendAriaLabel(metric)}
        />
      </div>
      <p className="admin-hint">
        Each point is a succeeded collect run, its band the 95% interval.
        {markers.length > 0 ? ' Dashed rules mark a prompt-version change.' : ''}
        {partial > 0
          ? ` ${partial === 1 ? '1 point comes from a run' : `${partial} points come from runs`} of part of the suite, drawn apart from the whole-suite lines.`
          : ''}
      </p>
    </>
  );
}

/**
 * One live expert (TOM-533): its version, the newest stored run of it at that
 * version (any model, whole or part of its questions), and its newest run.
 */
function ExpertRow({ e }: { e: EvalExpertStatus }) {
  const run = (r: NonNullable<EvalExpertStatus['current']>) => (
    <span className="eval-stack">
      <span>
        <RunLink id={r.runId} /> · {r.status.replace('_', ' ')}
        {r.split !== null ? ` · ${r.split}` : ''}
        {r.fullSelection ? '' : ' · part'}
      </span>
      <span className="admin-muted eval-sub">{formatDate(r.at)}</span>
    </span>
  );
  return (
    <tr data-testid={`eval-expert-${e.id}`}>
      <td className="eval-nowrap">
        <Link to={`/admin/evals/runs?role=${encodeURIComponent(e.id)}`}>{agentLabel(e.id)}</Link>
      </td>
      <td>
        <code>{e.version}</code>
      </td>
      <td>
        {e.current !== null ? (
          run(e.current)
        ) : (
          <EvalBadge tone="warn" title="No stored run of this expert at its deployed version">
            None yet
          </EvalBadge>
        )}
      </td>
      <td>
        {e.latest !== null ? (
          <span className="eval-stack">
            {run(e.latest)}
            <span className="admin-muted eval-sub">
              <code>{e.latest.version ?? '—'}</code>
            </span>
          </span>
        ) : (
          <span className="admin-muted">—</span>
        )}
      </td>
    </tr>
  );
}

export function AdminEvalsOverview() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<EvalOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    setError(null);
    evalsApi
      .overview(days)
      .then((res) => {
        if (!ignore) setData(res);
      })
      .catch((err) => {
        if (!ignore) setError(adminApiMessage(err, 'Failed to load the eval overview.'));
      });
    return () => {
      ignore = true;
    };
  }, [days]);

  const headline = data !== null ? notEvaluatedHeadline(data) : null;

  return (
    <section className="admin-section eval-page">
      <header className="admin-section-header">
        <h1>Evals</h1>
        <span className="admin-count">
          <Link to="/admin/evals/runs">All runs →</Link>
        </span>
        <GuideLink section="overview" />
      </header>
      <p className="admin-hint">
        Is what is deployed evaluated, how sure are we, and is quality drifting. A pairing is a
        deployed model in its role at the deployed prompt version. A version is a label and a
        fingerprint of the prompt and tools, and only a run of that exact version covers it.
      </p>
      <p className="eval-actions">
        {/* TOM-120: launch a run on the worker, inside a budget. */}
        <Link
          className="admin-button-affirm"
          to="/admin/evals/launch"
          data-testid="eval-launch-link"
        >
          Launch a run
        </Link>
      </p>

      <EvalError message={error} />
      {data === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {data !== null && headline !== null ? (
        <>
          <div
            className={`eval-headline eval-headline--${headline.tone}`}
            data-testid="eval-headline"
            role="status"
          >
            {headline.text}
          </div>

          <EvalSchedulePanel />

          <h2>Deployed pairings</h2>
          <div className="scroll-x">
            <table className="admin-table eval-table eval-pairings">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Role</th>
                  <th>Suite</th>
                  <th>Prompt</th>
                  <th>State</th>
                  <th>Covering run</th>
                  <th>Test score</th>
                </tr>
              </thead>
              <tbody>
                {sortPairings(data.pairings).map((p) => (
                  <PairingRow key={`${p.model}|${p.role}|${p.suite}|${p.split}`} p={p} />
                ))}
              </tbody>
            </table>
          </div>

          {data.experts.length > 0 ? (
            <>
              <h2>Experts behind Neo</h2>
              <p className="admin-hint">
                Each expert live behind Neo at the version deployed, and whether a run of it alone
                at that version is stored. <Link to="/admin/evals/launch">Launch a run</Link> to
                test one.
              </p>
              <div className="scroll-x">
                <table className="admin-table eval-table" data-testid="eval-experts">
                  <thead>
                    <tr>
                      <th>Expert</th>
                      <th>Version</th>
                      <th>Run at this version</th>
                      <th>Newest run</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.experts.map((e) => (
                      <ExpertRow key={e.id} e={e} />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}

          <h2>What is deployed</h2>
          <div className="eval-two-col">
            <dl className="admin-defs" data-testid="eval-deployed">
              <dt>Neo prompt</dt>
              <dd>
                <code>{data.deployed.neoPromptVersion}</code>
              </dd>
              <dt>Watch prompt</dt>
              <dd>
                <code>{data.deployed.watchPromptVersion}</code>
              </dd>
              <dt>Code</dt>
              <dd>
                <code>{shortSha(data.deployed.code.gitSha)}</code>
                {data.deployed.code.dirty === true ? ' (uncommitted changes)' : ''}
                {data.deployed.code.source === 'none' ? (
                  <span className="admin-muted"> not recorded in this build</span>
                ) : null}
              </dd>
            </dl>
            <div className="scroll-x">
              <table className="admin-table eval-table eval-table--compact eval-slots">
                <thead>
                  <tr>
                    <th>Setting</th>
                    <th>Model</th>
                    <th>Eval</th>
                  </tr>
                </thead>
                <tbody>
                  {sortSlots(data.slots).map((s) => (
                    <tr key={`${s.setting}|${s.alias ?? ''}|${s.model}`}>
                      <td className="eval-nowrap">
                        <code>
                          {s.setting === 'MODEL_CHOICE' ? `/model ${s.alias ?? ''}` : s.setting}
                        </code>
                      </td>
                      <td>
                        <code>{s.model}</code>
                        {s.label !== null ? (
                          <span className="admin-muted"> · {s.label}</span>
                        ) : null}
                      </td>
                      <td className={s.role === null ? 'admin-muted' : undefined}>
                        {slotCoverageText(s)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <h2>
            Eval spend{' '}
            <span className="admin-count admin-seg">
              {SPEND_WINDOWS.map((w) => (
                <button
                  key={w}
                  type="button"
                  className="admin-link-button"
                  disabled={w === days}
                  onClick={() => setDays(w)}
                >
                  {w}d
                </button>
              ))}
            </span>
          </h2>
          <dl className="admin-defs" data-testid="eval-spend">
            <dt>Last {data.spend.windowDays} days</dt>
            <dd className="admin-defs-lead">{formatUsd(data.spend.totalUsd)}</dd>
            <dt>Model and judge calls</dt>
            <dd>
              {formatInt(data.spend.calls)}
              {data.spend.errors > 0 ? ` (${formatInt(data.spend.errors)} errors)` : ''}
            </dd>
            <dt>Since</dt>
            <dd>{formatDate(data.spend.since)}</dd>
          </dl>
          {data.spend.byPurpose.length > 0 ? (
            <div className="eval-two-col">
              <div className="scroll-x">
                <table className="admin-table eval-table--compact">
                  <thead>
                    <tr>
                      <th>Purpose</th>
                      <th>Calls</th>
                      <th>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.spend.byPurpose.map((g) => (
                      <tr key={g.purpose}>
                        <td>{g.purpose}</td>
                        <td>{formatInt(g.calls)}</td>
                        <td>{formatUsd(g.costUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="scroll-x">
                <table className="admin-table eval-table--compact">
                  <thead>
                    <tr>
                      <th>Model</th>
                      <th>Calls</th>
                      <th>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.spend.byModel.map((g) => (
                      <tr key={g.model}>
                        <td>
                          <code>{g.model}</code>
                        </td>
                        <td>{formatInt(g.calls)}</td>
                        <td>{formatUsd(g.costUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="admin-empty">No eval or judge calls in this window.</p>
          )}

          <h2>Trends</h2>
          <Trends overview={data} />

          <p className="admin-hint eval-footnote">
            Grading that counts toward &quot;evaluated&quot;: {gradingLabel('cross_vendor')}. Read{' '}
            {formatDate(data.generatedAt)}.
          </p>
        </>
      ) : null}
    </section>
  );
}
